(() => {
  'use strict';
  if (customElements.get('kaya-motion')) return;
  class KayaMotion extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.motionAllowed = () => document.body?.dataset.motion !== 'false' && !matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.classList.add('is-kp-ready');
      this.setupReveals();
      this.setupStories();
      this.setupHeroParallax();
      const on = (target, event, fn, options = {}) => target?.addEventListener(event, fn, {...options, signal});
      on(document, 'shopify:section:load', () => { this.ready = false; this.disconnectedCallback(); this.connectedCallback(); });
    }
    setupReveals() {
      const targets = [...this.querySelectorAll('[data-kp-reveal]')];
      if (!this.motionAllowed() || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.isIntersecting) { entry.target.classList.remove('kp-wait'); observer.unobserve(entry.target); }
      }), {threshold: .08, rootMargin: '0px 0px -30px 0px'});
      this.revealObserver = observer;
      targets.forEach((element, index) => { element.style.transitionDelay = `${Math.min(index * 55, 220)}ms`; if (element.getBoundingClientRect().top > innerHeight * .82) element.classList.add('kp-wait'); observer.observe(element); });
    }
    setupHeroParallax() {
      if (!this.motionAllowed()) return;
      const figure = this.querySelector('[data-kp-depth]');
      if (!figure) return;
      const onMove = event => {
        const rect = figure.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - .5;
        const y = (event.clientY - rect.top) / rect.height - .5;
        figure.style.setProperty('--kp-x', `${x * 1.4}deg`); figure.style.setProperty('--kp-y', `${y * -1.4}deg`);
      };
      const onLeave = () => { figure.style.removeProperty('--kp-x'); figure.style.removeProperty('--kp-y'); };
      figure.addEventListener('pointermove', onMove, {signal:this.controller.signal});
      figure.addEventListener('pointerleave', onLeave, {signal:this.controller.signal});
      figure.style.transform = 'perspective(1100px) rotateX(var(--kp-y,0deg)) rotateY(var(--kp-x,0deg))';
    }
    setupStories() {
      this.stories = [...this.querySelectorAll('[data-kp-story]')];
      this.stories.forEach(story => {
        const scenes = [...story.querySelectorAll('[data-kp-scene]')];
        if (!scenes.length || !this.motionAllowed() || innerWidth < 900) return;
        story.classList.add('kp-story-enhanced');
        story.dataset.kpScenes = scenes.length;
        story.querySelector('[data-kp-progress]')?.style.setProperty('width', `${100 / scenes.length}%`);
        this.setStory(story, 0);
      });
      const update = () => { this.raf ||= requestAnimationFrame(() => { this.raf = 0; this.updateStories(); }); };
      this.updateStories = () => this.stories.forEach(story => {
        if (!story.classList.contains('kp-story-enhanced')) return;
        const rect = story.getBoundingClientRect();
        const range = Math.max(1, story.offsetHeight - innerHeight);
        const progress = Math.max(0, Math.min(1, -rect.top / range));
        const count = Number(story.dataset.kpScenes || 1);
        const index = Math.min(count - 1, Math.floor(progress * count));
        this.setStory(story, index, progress);
      });
      window.addEventListener('scroll', update, {passive:true,signal:this.controller.signal});
      window.addEventListener('resize', update, {passive:true,signal:this.controller.signal});
      update();
    }
    setStory(story, index, progress = 0) {
      story.querySelectorAll('[data-kp-scene]').forEach((scene, i) => { const active = i === index; scene.classList.toggle('is-current', active); scene.setAttribute('aria-hidden', String(!active)); });
      const progressEl = story.querySelector('[data-kp-progress]');
      if (progressEl) progressEl.style.width = `${Math.max(0, Math.min(100, ((index + 1) / Number(story.dataset.kpScenes || 1)) * 100))}%`;
      const image = story.querySelector('[data-kp-story-image]');
      if (image && this.motionAllowed()) image.style.transform = `rotate(${-2 + progress * 4}deg) translateY(${(0.5 - progress) * 14}px)`;
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null; this.revealObserver?.disconnect(); this.revealObserver = null; this.ready = false; if (this.raf) cancelAnimationFrame(this.raf);
    }
  }
  customElements.define('kaya-motion', KayaMotion);
})();
